// =============================================================================
// CART RECOMMENDATIONS — Horizon `Component` port of the "It's a match" cart-drawer
// cross-sell carousel. Atomic driver-swap of the bundled
// src/js/objects/global/cart-recommendations.js (which stays on disk, unbundled, until
// cutover): same fetch/cache flow, same DOM writes, same [hidden] semantics, same
// failure-swallowing. Loaded as native ESM via the `@theme/*` importmap
// (snippets/horizon-importmap.liquid).
//
// SCRIPT PLACEMENT (load-bearing): emitted from snippets/cart-drawer.liquid at a STABLE
// point — inside the persistent <cart-drawer>, OUTSIDE #CartDrawer — because both vendored
// cart re-render paths replace innerHTML strictly inside #CartDrawer (cart-drawer.js
// renderContents → '#CartDrawer'; CartDrawerItems qty/remove → '.drawer__inner'). Every
// cart mutation therefore constructs a FRESH <cart-recommendations>, the page-global
// registry re-upgrades it, and connectedCallback re-runs (re-fetch for the new seed) —
// the same re-upgrade contract the bundled driver relied on. Module scripts dedupe per
// URL, so the single stable emission executes exactly once per page.
//
// Behaviour (unchanged):
//   • connect → bind nav → fetch this.dataset.url (Product Recommendations API,
//     section_id=cart-recommendations, seeded by the shell snippet). Module-level
//     CART_REC_CACHE (keyed by full URL, raw response text) persists across drawer
//     re-renders, so rapid same-seed mutations skip the network.
//   • Empty recs → this.hidden = true — hides the WHOLE module (never an empty heading).
//     Load-bearing SCSS: src/scss/objects/global/_cart-recommendations.scss
//     `.cart-recommendations[hidden] { display: none; }` overrides the base display:flex.
//   • Success → swap the fetched track's innerHTML into the local track, scrollLeft = 0.
//   • ALL failures swallowed (empty catch; !response.ok returns WITHOUT caching) — this
//     element must NEVER throw into the cart flow. Hence NO `requiredRefs`: Component's
//     #updateRefs THROWS MissingRefError for missing required refs, which would violate
//     the never-throw contract. Plain refs + soft guards only.
//
// Architecture-only deltas vs the bundled module (zero behaviour change):
//   • extends Component (super.connectedCallback wires refs + MutationObserver + events);
//   • track / nav buttons resolved via `ref=` hooks (ref="track|prevButton|nextButton" in
//     snippets/cart-recommendations.liquid). The FETCHED doc's track stays a class query —
//     DOMParser documents have no Component refs.
//
// SOLE DRIVER (post Wave-1b integrate rebuild): the old compiled class is GONE from
// assets/global.autojs.js — this module is the only definition of <cart-recommendations>.
// The shell snippet's data-cart-rec-prev/next attributes are now INERT (no consumers);
// delete them at cutover. The `customElements.get()` guard below is plain double-define
// hygiene, not a handover gate.
//
// UNTRACKED new file (alongside assets/component.js + the other *-component.js ports) —
// re-verify it exists before any push; a parallel agent's git op on assets/ can remove it.
// =============================================================================
import { Component } from '@theme/component';

// Per-page cache so rapid cart mutations (each replacing the drawer) don't refetch the same URL.
const CART_REC_CACHE = new Map();

/**
 * The "It's a match" cart-drawer cross-sell carousel. Hydrates the inline shell
 * (snippets/cart-recommendations.liquid) with live recommendations and wires the
 * horizontal-scroll nav.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track] - the horizontal card rail; undefined if the shell markup
 *   drifts (soft-guarded — the module then simply leaves the server-rendered fallback).
 * @property {HTMLButtonElement} [prevButton]
 * @property {HTMLButtonElement} [nextButton]
 *
 * @extends {Component<Refs>}
 */
export class CartRecommendations extends Component {
  // Deliberately NO requiredRefs — a missing ref must degrade silently, never throw.

  connectedCallback() {
    super.connectedCallback(); // Component base: refs sync (synchronous) + declarative event wiring
    this.bindNav();
    this.loadRecommendations();
  }

  bindNav() {
    const { prevButton, nextButton } = this.refs;
    if (prevButton) prevButton.addEventListener('click', () => this.scrollByCard(-1));
    if (nextButton) nextButton.addEventListener('click', () => this.scrollByCard(1));
  }

  scrollByCard(direction) {
    const { track } = this.refs;
    if (!track) return;
    const card = track.querySelector('.cart-recommendations__card');
    // card width + the 12px track gap; fall back to ~80% of the rail if no card is present yet.
    const step = card
      ? card.getBoundingClientRect().width + 12
      : track.clientWidth * 0.8;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  async loadRecommendations() {
    const url = this.dataset.url;
    // Captured ONCE (same element identity across the await) — mirrors the old driver
    // caching `this.track` at connect.
    const { track } = this.refs;
    if (!url || !track) return;

    try {
      let html = CART_REC_CACHE.get(url);
      if (!html) {
        const response = await fetch(url);
        if (!response.ok) return;
        html = await response.text();
        CART_REC_CACHE.set(url, html);
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      // MUST stay a selector query — DOMParser docs have no refs.
      const freshTrack = doc.querySelector('.cart-recommendations__track');
      if (!freshTrack) return;

      if (!freshTrack.children.length) {
        // No recommendations came back — hide the whole module rather than show an empty heading.
        this.hidden = true;
        return;
      }

      track.innerHTML = freshTrack.innerHTML;
      track.scrollLeft = 0;
    } catch {
      // Network / parse failure: keep the server-rendered fallback; never throw into the cart flow.
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
